/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { AnyAction } from 'redux';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import {
    EXIT_PLAYER_THRESHOLD_TIME,
    SECONDS_IN_A_MINUTE,
    STILL_THERE_THRESHOLD_TIME,
} from '../../config';
import { Constants } from '../../core/constants';
import { Channel } from '../../core/entities/Channel';
import { ServicesClientImpl } from '../../core/interactors';
import { Log } from '../../core/logging/Log';
import { QoEAppLogger } from '../../core/logging/qoe/QoEAppLogger';
import { VuLogger } from '../../core/logging/VULogger';
import { ScreenControllerTypes } from '../../core/navigation/ScreenControllerTypes';
import { ScreenTaskControllerTypes } from '../../core/navigation/ScreenTaskControllerTypes';
import { PlaybackContentType } from '../../core/playback/enums';
import { PlaybackRestrictionType, PlaybackSpeed } from '../../core/videoplayer/enums';
import { TrickplayImpl } from '../../core/videoplayer/impls';
import { IPlayable } from '../../core/videoplayer/interfaces';
import { VideoPlaybackController } from '../../core/videoplayer/VideoPlaybackController';
import { resetAntiburnTimeout } from '../antiburnosd/antiburnosd.reducers';
import { OverlayTypes } from '../overlay/OverlayTypes';
import { showPCPinOverlayWithProgramRating } from '../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    setScreenTaskController,
    showGuideScreen,
    showLiveTvScreen,
    showPreviousScreen,
} from '../screens/screens.reducers';
import { hideSpinner } from '../spinner/spinner.reducers';
import { setCurrentActiveIndexAction } from '../videoplayerui/infobanner/infobanner.actions';
import { MediaButtonTypes } from '../videoplayerui/infobanner/mediabuttontypes';
import { PlaybackState, TrickPlayType } from '../videoplayerui/infobanner/PlaybackTypes';
import { hideErrorOSD } from '../videoplayerui/osd/osd.reducers';
import {
    hideStillThereOsdAction,
    isStillThereAction,
    resetStillThereTimeoutAction,
    setExitPlayerTimeoutAction,
    showStillThereOsdAction,
    videoPlayerIsMediaButtonPressedAction,
    videoPlayerResetRetryAction,
    videoPlayerSetPlaybackStateAction,
    videoPlayerToggleIsPlayingAction,
} from './videoplayer.actions';
import {
    HIDE_STILL_THERE_OSD,
    IS_STILL_THERE,
    SET_EXIT_PLAYER_TIMER,
    SHOW_STILL_THERE_OSD,
    UPDATE_STILL_THERE_TIMER,
    VIDEO_PLAYER_IS_PAUSE_BUTTON_PRESSED,
    VIDEO_PLAYER_RESET_RETRY,
    VIDEO_PLAYER_SET_PLAYBACK_STATE,
    VIDEO_PLAYER_TOGGLE_IS_PLAYING,
} from './videoplayer.types';

const initialState: {
    channel: Channel;
    retry: number;
    isPlaying: boolean;
    exitPlayerTimer: any;
    stillThereTimer: any;
    isStillThere: boolean;
    playbackState: PlaybackState;
    isStillThereOsdActive: boolean;
    mediaButtonPressed: {
        type: TrickPlayType;
        isMediaButtonPressed: boolean;
    };
} = {
    channel: {} as Channel,
    retry: 0,
    isPlaying: true,
    playbackState: PlaybackState.PLAY,
    exitPlayerTimer: null,
    stillThereTimer: null,
    isStillThereOsdActive: false,
    isStillThere: true,
    mediaButtonPressed: {
        type: TrickPlayType.NONE,
        isMediaButtonPressed: false,
    },
};

const TAG: string = 'VIDEOPLAYER';

const videoPlayerReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case VIDEO_PLAYER_RESET_RETRY:
            return {
                ...state,
                retry: 0,
            };

        case VIDEO_PLAYER_TOGGLE_IS_PLAYING:
            return {
                ...state,
                isPlaying: payload.isPlaying,
            };
        case VIDEO_PLAYER_SET_PLAYBACK_STATE:
            return {
                ...state,
                playbackState: payload.playbackState,
            };
        case SHOW_STILL_THERE_OSD:
        case HIDE_STILL_THERE_OSD:
            return {
                ...state,
                isStillThereOsdActive: payload.isStillThereOsdActive,
            };
        case UPDATE_STILL_THERE_TIMER:
            return {
                ...state,
                stillThereTimer: payload.stillThereTimer,
            };
        case IS_STILL_THERE:
            return {
                ...state,
                isStillThere: payload.isStillThere,
            };

        case SET_EXIT_PLAYER_TIMER:
            return {
                ...state,
                exitPlayerTimer: payload.exitPlayerTimer,
            };
        case VIDEO_PLAYER_IS_PAUSE_BUTTON_PRESSED:
            return {
                ...state,
                mediaButtonPressed: payload.mediaButtonPressed,
            };

        default:
            return state;
    }
};

export const hideNotSupportedText = () => (dispatch: Function, getState: Function) => {
    const { mediaButtonPressed } = getState().videoPlayer;
    if (mediaButtonPressed.isMediaButtonPressed) {
        dispatch(
            videoPlayerIsMediaButtonPressedAction({
                type: TrickPlayType.NONE,
                isMediaButtonPressed: false,
            }),
        );
    }
};

export const startVideoPlayback = (playable: IPlayable) => (dispatch: Function) => {
    // TODO : recording id will be replaced as part of KST-7545
    dispatch(showLiveTvScreen());
    dispatch(resetAntiburnTimeout());
    VideoPlaybackController.getInstance().startPlayback(playable);
    VideoPlaybackController.getInstance().maximize();
};

export const toggleClosedCaption = () => () => {
    Diagnostics.info(TAG, 'toggleClosedCaption');
    Log.i(TAG, 'toggleClosedCaption');
    VideoPlaybackController.getInstance().toggleClosedCaption();
};

export const changeClosedCaptionLanguage = (selectedIndex: number) => () => {
    VideoPlaybackController.getInstance().setTextTrackByIndex(selectedIndex);
};

export const changeAudioTrack = (selectedIndex: number) => () => {
    Diagnostics.info(TAG, 'changeAudioTrack at index::', selectedIndex);
    Log.i(TAG, 'changeAudioTrack at index::', selectedIndex);
    VideoPlaybackController.getInstance().setAudioTrack(selectedIndex);
};

export const resetPlaybackRetry = () => (dispatch: Function) => {
    dispatch(videoPlayerResetRetryAction());
};

export const toggleIsPlaying = () => (dispatch: Function, getState: Function) => {
    const { isPlaying } = getState().videoPlayer;
    const oVideoPlaybackController = VideoPlaybackController.getInstance();
    if (isPlaying) {
        oVideoPlaybackController.pause(true);
        dispatch(disableStillThereTimer());
        if (
            oVideoPlaybackController.getPlayable()?.playbackContentType !== PlaybackContentType.LIVE
        ) {
            oVideoPlaybackController.updateLastViewedPosition();
        }
    } else {
        oVideoPlaybackController.resume();
        dispatch(resetStillThereTimeout(true));
        if (oVideoPlaybackController.isPlaybackStarted()) {
            oVideoPlaybackController.getTrickplayControl().stopScrubbing();
        }
    }
    Diagnostics.info(TAG, 'toggleIsPlaying:', !isPlaying);
    Log.i(TAG, 'toggleIsPlaying:', !isPlaying);
    dispatch(videoPlayerToggleIsPlayingAction(!isPlaying));
    dispatch(
        videoPlayerSetPlaybackStateAction(isPlaying ? PlaybackState.PAUSE : PlaybackState.PLAY),
    );
    VuLogger.logTrickPlayEvent(
        isPlaying ? VuLogger.TrickplayMode.PAUSE : VuLogger.TrickplayMode.PLAY,
        oVideoPlaybackController.getPlayerCurrentTime() * Constants.MILLISECOND_IN_ONE_SECOND,
    );
};

export const resetPlaybackParamsVideoplayer = () => (dispatch: Function) => {
    VideoPlaybackController.getInstance().getTrickplayControl().stopScrubbing();
    dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.PLAY));
    dispatch(videoPlayerToggleIsPlayingAction(true));
    dispatch(resetStillThereTimeout(true));
};

export const handleTrickplaySpeedOnNetworkConnection =
    () => (dispatch: Function, getState: Function) => {
        const { playbackState } = getState().videoPlayer;
        const oIVideoPlayerController = VideoPlaybackController.getInstance();
        const oTrickplayControl = oIVideoPlayerController.getTrickplayControl();

        const PlaybackStateToSpeedMap: Record<PlaybackState, PlaybackSpeed | number> = {
            [PlaybackState.RW3]: PlaybackSpeed.SPEED_REWIND_3,
            [PlaybackState.RW2]: PlaybackSpeed.SPEED_REWIND_2,
            [PlaybackState.RW1]: PlaybackSpeed.SPEED_REWIND_1,
            [PlaybackState.FF1]: PlaybackSpeed.SPEED_FASTFORWARD_1,
            [PlaybackState.FF2]: PlaybackSpeed.SPEED_FASTFORWARD_2,
            [PlaybackState.FF3]: PlaybackSpeed.SPEED_FASTFORWARD_3,
            [PlaybackState.PAUSE]: 0,
            [PlaybackState.PLAY]: 0,
            [PlaybackState.STOP]: 0,
        };

        const playbackSpeed = PlaybackStateToSpeedMap[playbackState];

        if (playbackSpeed > 0) {
            dispatch(setCurrentActiveIndexAction(MediaButtonTypes.FORWARD_BUTTON_INDEX));
            oTrickplayControl.fastForward(playbackSpeed);
        } else if (playbackSpeed < 0) {
            dispatch(setCurrentActiveIndexAction(MediaButtonTypes.REWIND_BUTTON_INDEX));
            oTrickplayControl.rewind(playbackSpeed);
        } else if (!oIVideoPlayerController.isForcePausedByUser()) {
            oIVideoPlayerController.resume();
        }
    };

export const handleFastForward = () => (dispatch: Function, getState: Function) => {
    const oVideoPlaybackController = VideoPlaybackController.getInstance();
    const { playbackState } = getState().videoPlayer;
    const { isSpinnerActive } = getState().spinner;
    dispatch(hideNotSupportedText());
    const oTrickplayControl = oVideoPlaybackController.getTrickplayControl();

    if (isSpinnerActive) dispatch(hideSpinner());
    Log.i(TAG, `Playing with ${playbackState}`);
    Diagnostics.info(TAG, `Playing with ${playbackState}`);
    if (playbackState === PlaybackState.FF1) {
        oTrickplayControl.fastForward(PlaybackSpeed.SPEED_FASTFORWARD_2);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.FF2));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.FAST_FORWARD_2,
            oVideoPlaybackController.getPlayerCurrentTime(),
        );
    } else if (playbackState === PlaybackState.FF2) {
        oTrickplayControl.fastForward(PlaybackSpeed.SPEED_FASTFORWARD_3);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.FF3));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.FAST_FORWARD_3,
            oVideoPlaybackController.getPlayerCurrentTime(),
        );
    } else if (playbackState === PlaybackState.FF3) {
        Diagnostics.info(TAG, `Already playing with maximum fast forward speed 3.`);
        Log.i(TAG, `Already playing with maximum fast forward speed 3.`);
    } else if (playbackState === PlaybackState.RW3) {
        oTrickplayControl.rewind(PlaybackSpeed.SPEED_REWIND_2);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.RW2));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.REWIND_2,
            oVideoPlaybackController.getPlayerCurrentTime(),
        );
    } else if (playbackState === PlaybackState.RW2) {
        oTrickplayControl.rewind(PlaybackSpeed.SPEED_REWIND_1);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.RW1));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.REWIND_1,
            oVideoPlaybackController.getPlayerCurrentTime(),
        );
    } else if (playbackState === PlaybackState.RW1) {
        dispatch(toggleIsPlaying());
    } else {
        oVideoPlaybackController.mute(true);
        dispatch(videoPlayerToggleIsPlayingAction(false));
        oTrickplayControl.fastForward(PlaybackSpeed.SPEED_FASTFORWARD_1);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.FF1));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.FAST_FORWARD_1,
            oVideoPlaybackController.getPlayerCurrentTime(),
        );
        QoEAppLogger.updateHealthStatsMetricsForTrickPlay();
    }
};

export const handleRewind = () => (dispatch: Function, getState: Function) => {
    const { playbackState } = getState().videoPlayer;
    const { isSpinnerActive } = getState().spinner;
    const oVideoPlaybackController = VideoPlaybackController.getInstance();
    const oTrickplayControl = oVideoPlaybackController.getTrickplayControl();
    dispatch(hideNotSupportedText());
    if (isSpinnerActive) dispatch(hideSpinner());
    Log.i(TAG, `Playing with ${playbackState}`);
    Diagnostics.info(TAG, `Playing with ${playbackState}`);
    if (playbackState === PlaybackState.RW1) {
        oTrickplayControl.rewind(PlaybackSpeed.SPEED_REWIND_2);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.RW2));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.REWIND_2,
            oVideoPlaybackController.getPlayerCurrentTime(),
        );
    } else if (playbackState === PlaybackState.RW2) {
        oTrickplayControl.rewind(PlaybackSpeed.SPEED_REWIND_3);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.RW3));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.REWIND_3,
            oVideoPlaybackController.getPlayerCurrentTime(),
        );
    } else if (playbackState === PlaybackState.RW3) {
        Diagnostics.info(TAG, `Already playing with maximum rewind speed 3.`);
        Log.i(TAG, `Already playing with maximum rewind speed 3.`);
    } else if (playbackState === PlaybackState.FF3) {
        oTrickplayControl.fastForward(PlaybackSpeed.SPEED_FASTFORWARD_2);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.FF2));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.FAST_FORWARD_2,
            oVideoPlaybackController.getPlayerCurrentTime(),
        );
    } else if (playbackState === PlaybackState.FF2) {
        oTrickplayControl.fastForward(PlaybackSpeed.SPEED_FASTFORWARD_1);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.FF1));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.FAST_FORWARD_1,
            oVideoPlaybackController.getPlayerCurrentTime(),
        );
    } else if (playbackState === PlaybackState.FF1) {
        dispatch(toggleIsPlaying());
    } else {
        oVideoPlaybackController.mute(true);
        dispatch(videoPlayerToggleIsPlayingAction(false));
        oTrickplayControl.rewind(PlaybackSpeed.SPEED_REWIND_1);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.RW1));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.REWIND_1,
            oVideoPlaybackController.getPlayerCurrentTime(),
        );
        QoEAppLogger.updateHealthStatsMetricsForTrickPlay();
    }
};

export const handleReplay = () => (dispatch: Function, getState: Function) => {
    const { playbackState } = getState().videoPlayer;
    const oVideoPlaybackController = VideoPlaybackController.getInstance();
    const trickplayControl = oVideoPlaybackController.getTrickplayControl();
    dispatch(hideNotSupportedText());
    QoEAppLogger.setIsUserTriggerAction(true);
    Diagnostics.info(TAG, 'handleReplay');
    Log.i(TAG, 'handleReplay');
    if (
        playbackState === PlaybackState.RW1 ||
        playbackState === PlaybackState.RW2 ||
        playbackState === PlaybackState.RW3
    ) {
        const trickplayImplIns = new TrickplayImpl(
            oVideoPlaybackController.getPlayable(),
            oVideoPlaybackController.getPlaybackIntervalInfo(),
            oVideoPlaybackController.getMetadata(),
        );
        trickplayControl.skipToTickMark(
            false,
            trickplayImplIns.getTickmarkDuration(
                trickplayImplIns.getProgramDuration() / SECONDS_IN_A_MINUTE,
            ),
            oVideoPlaybackController.isStartOver() || oVideoPlaybackController.isNdvrLive(),
        );
    } else {
        trickplayControl.replay();
        if (playbackState !== PlaybackState.PLAY) dispatch(toggleIsPlaying());
    }
};

export const handleAdvance = () => (dispatch: Function, getState: Function) => {
    const { playbackState } = getState().videoPlayer;
    const oVideoPlaybackController = VideoPlaybackController.getInstance();
    const trickplayControl = oVideoPlaybackController.getTrickplayControl();
    const trickplayImplIns = new TrickplayImpl(
        oVideoPlaybackController.getPlayable(),
        oVideoPlaybackController.getPlaybackIntervalInfo(),
        oVideoPlaybackController.getMetadata(),
    );
    dispatch(hideNotSupportedText());
    QoEAppLogger.setIsUserTriggerAction(true);
    if (
        playbackState === PlaybackState.FF1 ||
        playbackState === PlaybackState.FF2 ||
        playbackState === PlaybackState.FF3
    ) {
        trickplayControl.skipToTickMark(
            true,
            trickplayImplIns.getTickmarkDuration(
                trickplayImplIns.getProgramDuration() / SECONDS_IN_A_MINUTE,
            ),
            oVideoPlaybackController.isStartOver() || oVideoPlaybackController.isNdvrLive(),
        );
    } else if (
        playbackState === PlaybackState.RW1 ||
        playbackState === PlaybackState.RW2 ||
        playbackState === PlaybackState.RW3
    ) {
        trickplayControl.skipToTickMark(
            false,
            trickplayImplIns.getTickmarkDuration(
                trickplayImplIns.getProgramDuration() / SECONDS_IN_A_MINUTE,
            ),
            oVideoPlaybackController.isStartOver() || oVideoPlaybackController.isNdvrLive(),
        );
    } else {
        trickplayControl.advance();
        if (playbackState !== PlaybackState.PLAY) dispatch(toggleIsPlaying());
    }
};

export const showStillThereOsd = () => (dispatch: Function) => {
    Diagnostics.debug(
        TAG,
        'showStillThereOsd {screenTaskController: ',
        ScreenTaskControllerTypes.STILL_THERE_OSD_TASKS,
        '}',
    );
    Log.d(
        TAG,
        'showStillThereOsd {screenTaskController: ',
        ScreenTaskControllerTypes.STILL_THERE_OSD_TASKS,
        '}',
    );
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.STILL_THERE_OSD_TASKS));
    VuLogger.logNotifyEvent(
        VuLogger.NotifyEventSourceType.STILL_THERE_OSD,
        VuLogger.NotifyEventSourceName.STILL_THERE,
    );
    dispatch(showStillThereOsdAction());
};

export const hideStillThereOsd = () => (dispatch: Function, getState: Function) => {
    const { uiProperties: uiProps, isOverlayDialogActive } = getState().overlayDialog;

    const isAudioTrackOverlayActive =
        isOverlayDialogActive && uiProps.overlayType === OverlayTypes.AUDIO_TRACK;

    const isSubtitleAndCCLanguageOverlayActive =
        isOverlayDialogActive && uiProps.overlayType === OverlayTypes.SUBTITLE_AND_CC_LANGUAGE;

    Diagnostics.info(TAG, 'hideStillThereOsd ');
    Log.i(TAG, 'hideStillThereOsd ');
    if (isSubtitleAndCCLanguageOverlayActive) {
        Diagnostics.debug(
            TAG,
            'hideStillThereOsd {screenTaskController: ',
            ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY,
            '}',
        );
        Log.d(
            TAG,
            'hideStillThereOsd {screenTaskController: ',
            ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY,
            '}',
        );
        dispatch(
            setScreenTaskController(ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY),
        );
    } else if (isAudioTrackOverlayActive) {
        Diagnostics.debug(
            TAG,
            'hideStillThereOsd {screenTaskController: ',
            ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY,
            '}',
        );
        Log.d(
            TAG,
            'hideStillThereOsd {screenTaskController: ',
            ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY,
            '}',
        );
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY));
    } else {
        Diagnostics.debug(
            TAG,
            'hideStillThereOsd {screenTaskController: ',
            ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS,
            '}',
        );
        Log.d(
            TAG,
            'hideStillThereOsd {screenTaskController: ',
            ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS,
            '}',
        );
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS));
    }
    dispatch(hideStillThereOsdAction());
};

export const disableStillThereTimer = () => (dispatch: Function, getState: Function) => {
    const { videoPlayer } = getState();
    const { stillThereTimer, exitPlayerTimer, isStillThereOsdActive } = videoPlayer;

    dispatch(isStillThereAction(false));

    if (isStillThereOsdActive) dispatch(hideStillThereOsd());

    if (stillThereTimer) clearTimeout(stillThereTimer);
    dispatch(resetStillThereTimeoutAction(null));

    if (exitPlayerTimer) clearTimeout(exitPlayerTimer);
    dispatch(setExitPlayerTimeoutAction(null));

    Diagnostics.info(TAG, 'disableStillThereTimer ');
    Log.i(TAG, 'disableStillThereTimer ');
};

export const resetStillThereTimeout =
    (isEnable?: boolean) => (dispatch: Function, getState: Function) => {
        if (isEnable) dispatch(isStillThereAction(isEnable));
        const { videoPlayer, screens } = getState();
        const { screenController } = screens;
        const { stillThereTimer, exitPlayerTimer, isStillThereOsdActive, isStillThere } =
            videoPlayer;
        const isStillThereEnable = isEnable || isStillThere;
        if (!isStillThereOsdActive) {
            if (stillThereTimer) clearTimeout(stillThereTimer);
            if (exitPlayerTimer) clearTimeout(exitPlayerTimer);
            if (screenController === ScreenControllerTypes.VIDEO_PLAYER && isStillThereEnable) {
                const timer = setTimeout(() => {
                    const { isErrorOSDActive } = getState().errorOsd;
                    if (!isErrorOSDActive) {
                        dispatch(showStillThereOsd());
                        dispatch(setExitPlayerTimeout());
                    }
                }, STILL_THERE_THRESHOLD_TIME);
                dispatch(resetStillThereTimeoutAction(timer));
            } else {
                if (stillThereTimer) dispatch(resetStillThereTimeoutAction(null));
                if (exitPlayerTimer) dispatch(setExitPlayerTimeoutAction(null));
            }
        }
        Diagnostics.info(TAG, 'resetStillThereTimeout ');
        Log.i(TAG, 'resetStillThereTimeout ');
    };

export const setExitPlayerTimeout = () => (dispatch: Function, getState: Function) => {
    const { videoPlayer } = getState();
    const { exitPlayerTimer } = videoPlayer;
    if (exitPlayerTimer) clearTimeout(exitPlayerTimer);
    const timer = setTimeout(() => {
        dispatch(hideStillThereOsd());
        dispatch(showPreviousScreen());
        dispatch(resetAntiburnTimeout());
    }, EXIT_PLAYER_THRESHOLD_TIME);
    dispatch(setExitPlayerTimeoutAction(timer));
    Diagnostics.info(TAG, 'setExitPlayerTimeout ');
    Log.i(TAG, 'setExitPlayerTimeout ');
};

/**
 * Helper function to determine if the current content is adult-rated
 * @returns boolean indicating if content is adult-rated and adult content hiding is enabled
 */
const isContentAdultRated = (): boolean => {
    const metadata = VideoPlaybackController.getInstance().getMetadata();
    const { airingOffers, offerPreview } = metadata;

    const services = new ServicesClientImpl();

    const isOfferRatedAdult =
        airingOffers?.[0]?.parentalControls?.isAdult ??
        offerPreview?.contentPreview?.isAdult ??
        false;

    return isOfferRatedAdult && services.shouldHideMetadata();
};

export const triggerPlaybackOnPinVerify = () => (dispatch: Function) => {
    if (
        VideoPlaybackController.getInstance().getRestrictionType() === PlaybackRestrictionType.ADULT
    ) {
        dispatch(hideErrorOSD(true));
        VideoPlaybackController.getInstance().restorePlayback();
    }
};

export const showPCPinOverlayForVideoPlayer =
    (fnCallback?: Function, onSuccess: Function = triggerPlaybackOnPinVerify) =>
    (dispatch: Function) => {
        if (!VideoPlaybackController.getInstance().isNetworkConnected()) {
            return;
        }
        const metadata = VideoPlaybackController.getInstance().getMetadata();
        const services = new ServicesClientImpl();

        const isAdult = isContentAdultRated();
        const restrictedRatingName = services.getRestrictedRatingName(metadata);

        let fnOnSuccess = onSuccess;
        if (fnCallback) {
            fnOnSuccess = () => () => {
                dispatch(onSuccess());
                dispatch(fnCallback());
            };
        }
        dispatch(showPCPinOverlayWithProgramRating(fnOnSuccess, isAdult, restrictedRatingName));
    };

export const isAdultContentPlaybackActive = (): boolean => {
    return (
        VideoPlaybackController.getInstance().getRestrictionType() === PlaybackRestrictionType.ADULT
    );
};

export const isAdultAndUnsubscribedContentPlaybackActive = (): boolean => {
    return (
        VideoPlaybackController.getInstance().getRestrictionType() ===
            PlaybackRestrictionType.UNSUBSCRIBED && isContentAdultRated()
    );
};

export default videoPlayerReducer;
